import { useTranslation } from 'react-i18next';
import { Icon } from './Icon';
import { MoleculeFormulaLabel } from './MoleculeFormulaLabel';
import { IsomerPicker } from './panels/IsomerPicker';
import { useBiopolymerComposer } from '../hooks/useBiopolymerComposer';
import type { useMoleculeSearch } from '../hooks/useMoleculeSearch';

interface BiopolymerComposerProps {
  readonly search: ReturnType<typeof useMoleculeSearch>;
  readonly onBack: () => void;
}

// #150: the peptide-only biopolymer composer, per #133's decision record. Simpler than
// the former ReactionComposer (retired, #705) and IllustrationComposer in one respect -- a residue is just an ordered reference
// to an existing Molecule, no depiction/canvas/role concept -- so this reuses IsomerPicker
// (wiring both onSelect and onSelectMolecule to the same "append as the next residue" handler,
// since a residue only ever needs a moleculeId) rather than needing a bespoke picker.
export function BiopolymerComposer({ search, onBack }: BiopolymerComposerProps) {
  const { t } = useTranslation();
  const composer = useBiopolymerComposer();

  return (
    <section className="reaction-composer" aria-labelledby="biopolymer-composer-heading">
      <div className="reaction-composer-header">
        <h2 id="biopolymer-composer-heading">{t('biopolymerComposer.heading')}</h2>
        <button
          type="button"
          className="icon-button"
          onClick={onBack}
          aria-label={t('biopolymerComposer.backToEditor')}
          title={t('biopolymerComposer.backToEditor')}
        >
          <Icon name="arrowBack" />
        </button>
      </div>

      <div className="reaction-column">
        <h3>{t('biopolymerComposer.sequence')}</h3>
        {composer.residues.length === 0 ? (
          <p className="reaction-column-empty">{t('biopolymerComposer.noneYet')}</p>
        ) : null}
        <ol className="reaction-participant-list">
          {composer.residues.map((residue, index) => (
            <li key={residue.localId} className="reaction-participant">
              <span className="biopolymer-residue-position">{index + 1}</span>
              <MoleculeFormulaLabel moleculeId={residue.moleculeId} />
              <div role="group" aria-label={t('biopolymerComposer.reorderAriaLabel')}>
                <button
                  type="button"
                  className="icon-button"
                  aria-label={t('biopolymerComposer.moveUp')}
                  title={t('biopolymerComposer.moveUp')}
                  onClick={() => composer.moveResidue(residue.localId, 'up')}
                  disabled={index === 0}
                >
                  <Icon name="arrowUpward" />
                </button>
                <button
                  type="button"
                  className="icon-button"
                  aria-label={t('biopolymerComposer.moveDown')}
                  title={t('biopolymerComposer.moveDown')}
                  onClick={() => composer.moveResidue(residue.localId, 'down')}
                  disabled={index === composer.residues.length - 1}
                >
                  <Icon name="arrowDownward" />
                </button>
              </div>
              <button
                type="button"
                className="icon-button"
                aria-label={t('biopolymerComposer.remove')}
                title={t('biopolymerComposer.remove')}
                onClick={() => composer.removeResidue(residue.localId)}
              >
                <Icon name="close" />
              </button>
            </li>
          ))}
        </ol>
      </div>

      <IsomerPicker
        search={search}
        onSelect={(_isomerId, moleculeId) => void composer.addResidue(moleculeId)}
        onSelectMolecule={(moleculeId) => void composer.addResidue(moleculeId)}
      />

      <div className="reaction-composer-actions">
        <button
          type="button"
          className="icon-button"
          disabled={!composer.canSave}
          onClick={() => void composer.save()}
          aria-label={
            composer.saveState.state === 'saving'
              ? t('biopolymerComposer.saving')
              : t('biopolymerComposer.saveBiopolymer')
          }
          title={
            composer.saveState.state === 'saving'
              ? t('biopolymerComposer.saving')
              : t('biopolymerComposer.saveBiopolymer')
          }
        >
          <Icon name="save" />
        </button>
        {composer.residues.length === 0 ? (
          <p className="reaction-composer-hint">{t('biopolymerComposer.minResiduesHint')}</p>
        ) : null}
        {composer.saveState.state === 'success' ? (
          <p className="import-success" role="status">
            {composer.saveState.message}
          </p>
        ) : null}
        {composer.saveState.state === 'error' ? (
          <p className="import-error" role="alert">
            {composer.saveState.message}
          </p>
        ) : null}
      </div>
    </section>
  );
}
